import {
  Lock as LockIcon,
  Notifications as NotificationsIcon,
  RestartAlt as RefreshIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import { auth } from '../firebase';
import { colors } from '../theme';

const DietitianSettingsPage: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);

  // Şifre Değiştirme
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Bildirim Tercihleri
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [appointmentReminders, setAppointmentReminders] = useState(true);
  const [newClientNotifications, setNewClientNotifications] = useState(true);
  const [messageNotifications, setMessageNotifications] = useState(true);

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      showError('Yeni şifreler eşleşmiyor!', 'Şifre Hatası');
      return;
    }

    if (newPassword.length < 6) {
      showError('Yeni şifre en az 6 karakter olmalıdır!', 'Şifre Hatası');
      return;
    }

    try {
      setLoading(true);
      const user = auth.currentUser;

      if (user && user.email) {
        const { EmailAuthProvider, reauthenticateWithCredential } =
          await import('firebase/auth');
        const credential = EmailAuthProvider.credential(
          user.email,
          currentPassword
        );
        await reauthenticateWithCredential(user, credential);

        const { updatePassword } = await import('firebase/auth');
        await updatePassword(user, newPassword);

        showSuccess('Şifreniz başarıyla değiştirildi!', 'Başarılı');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (error: any) {
      if (error.code === 'auth/wrong-password') {
        showError('Mevcut şifreniz yanlış!', 'Şifre Hatası');
      } else {
        showError(
          'Şifre değiştirilirken bir hata oluştu.',
          'Güncelleme Hatası'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotifications = () => {
    showSuccess('Bildirim tercihleri kaydedildi!', 'Başarılı');
  };

  const handleResetNotifications = () => {
    setEmailNotifications(true);
    setAppointmentReminders(true);
    setNewClientNotifications(true);
    setMessageNotifications(true);
    showSuccess('Bildirim tercihleri varsayılana döndürüldü!', 'Sıfırlandı');
  };

  return (
    <Box sx={{ pb: 4 }}>
      {/* Başlık */}
      <Box
        sx={{
          mb: 4,
          p: 3,
          bgcolor: 'white',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: colors.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SettingsIcon sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ color: colors.text.primary }}
            >
              Ayarlar
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Hesap ve tercih ayarlarınızı yönetin
            </Typography>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3} alignItems="stretch">
        {/* Şifre Değiştirme */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card
            sx={{
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    bgcolor: colors.alpha.primary10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <LockIcon sx={{ color: colors.primary.main, fontSize: 22 }} />
                </Box>
                <Typography variant="h6" fontWeight={600}>
                  Şifre Değiştir
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Box display="flex" flexDirection="column" gap={2} sx={{ flex: 1 }}>
                <Alert severity="info" sx={{ fontSize: '0.85rem', py: 1 }}>
                  Güvenliğiniz için düzenli olarak şifrenizi değiştirmenizi öneririz.
                </Alert>

                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label="Mevcut Şifre"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  variant="outlined"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />

                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label="Yeni Şifre"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  variant="outlined"
                  helperText="En az 6 karakter"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />

                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label="Yeni Şifre (Tekrar)"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  variant="outlined"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />

                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleChangePassword}
                  disabled={
                    loading ||
                    !currentPassword ||
                    !newPassword ||
                    !confirmPassword
                  }
                  sx={{
                    mt: 'auto',
                    bgcolor: colors.primary.main,
                    color: 'white',
                    py: 1.25,
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': { bgcolor: colors.hover.primary },
                  }}
                >
                  Şifreyi Güncelle
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Bildirim Tercihleri */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card
            sx={{
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    bgcolor: colors.alpha.primary10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <NotificationsIcon sx={{ color: colors.primary.main, fontSize: 22 }} />
                </Box>
                <Typography variant="h6" fontWeight={600}>
                  Bildirim Tercihleri
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2} sx={{ flex: 1 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: colors.background.light,
                    borderRadius: 2,
                    border: `1px solid ${colors.border.light}`,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={emailNotifications}
                        onChange={(e) => setEmailNotifications(e.target.checked)}
                        color="success"
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography fontWeight={600} fontSize="0.9rem">
                          Email Bildirimleri
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Genel bilgilendirme emaillerini al
                        </Typography>
                      </Box>
                    }
                  />
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: colors.background.light,
                    borderRadius: 2,
                    border: `1px solid ${colors.border.light}`,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={appointmentReminders}
                        onChange={(e) => setAppointmentReminders(e.target.checked)}
                        color="success"
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography fontWeight={600} fontSize="0.9rem">
                          Randevu Hatırlatmaları
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Yaklaşan randevular için bildirim al
                        </Typography>
                      </Box>
                    }
                  />
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: colors.background.light,
                    borderRadius: 2,
                    border: `1px solid ${colors.border.light}`,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newClientNotifications}
                        onChange={(e) => setNewClientNotifications(e.target.checked)}
                        color="success"
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography fontWeight={600} fontSize="0.9rem">
                          Yeni Danışan Bildirimleri
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Yeni danışan eklendiğinde bildirim al
                        </Typography>
                      </Box>
                    }
                  />
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: colors.background.light,
                    borderRadius: 2,
                    border: `1px solid ${colors.border.light}`,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={messageNotifications}
                        onChange={(e) => setMessageNotifications(e.target.checked)}
                        color="success"
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography fontWeight={600} fontSize="0.9rem">
                          Mesaj Bildirimleri
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Yeni mesaj geldiğinde bildirim al
                        </Typography>
                      </Box>
                    }
                  />
                </Paper>

                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={handleResetNotifications}
                    sx={{
                      borderColor: colors.primary.main,
                      color: colors.primary.main,
                      borderRadius: 2,
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: colors.hover.primary,
                        bgcolor: colors.alpha.primary05,
                      },
                    }}
                  >
                    Sıfırla
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveNotifications}
                    sx={{
                      bgcolor: colors.primary.main,
                      borderRadius: 2,
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': { bgcolor: colors.hover.primary },
                    }}
                  >
                    Kaydet
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DietitianSettingsPage;
